import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatTabGroup } from '@angular/material/tabs';

import { SnCamera, SnMapView } from '@lib/api/entities';
import { SnCamerasAPI, SnMapViewsService } from '@lib/api/services';
import { SnCurrentUser } from '@lib/auth';
import { SnCad } from '@lib/cad';

import { CameraSearchDialogComponent } from '../camera-search-dialog';

@Component({
  selector: 'sn-camera-view-tabs',
  templateUrl: './camera-view-tabs.component.html',
  styleUrls: ['./camera-view-tabs.component.scss'],
})
export class CameraViewTabsComponent implements OnInit, OnDestroy {
  @ViewChild(MatTabGroup, { static: false }) public tabGroup: MatTabGroup;

  @Input() public views: SnMapView[] = [];
  @Input() public activeCads: SnCad[] = [];
  @Input() public activeIndex: number = 0;
  @Output() public readonly activateView = new EventEmitter<SnMapView>();
  @Output() public readonly viewRemoved = new EventEmitter<void>();

  public currentUserId: string;
  private readonly _destroy$ = new Subject<void>();

  constructor(
    private readonly _mapViewsService: SnMapViewsService,
    private readonly _camerasAPI: SnCamerasAPI,
    private readonly _dialog: MatDialog,
    private readonly _currentUser: SnCurrentUser,
    private readonly _cd: ChangeDetectorRef,
  ) {
    this.currentUserId = this._currentUser.id;
  }

  public ngOnInit(): void {}

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  public canArchiveTab(tab: SnMapView, index: number): boolean {
    return this.activeIndex === index + 1 && tab?.user?.id === this.currentUserId && !tab.archived;
  }

  public trackView(index: number, item: SnMapView): string {
    return item.id;
  }

  public activate(index: number): void {
    setTimeout(() => {
      this.activeIndex = index;
    });
    if (index > 0) {
      const view = this.views[index - 1];
      if (view) {
        this.activateView.next(view);
      }
    }
  }

  public addCameras(view: SnMapView): void {
    if (view.cameras?.length) {
      this._camerasAPI
        .getCameraList({
          id__in: view.cameras.join(','),
        })
        .pipe(takeUntil(this._destroy$))
        .subscribe({
          next: (response) => {
            this._openCameraSearchDialog(view, response.results);
          },
        });
    } else {
      this._openCameraSearchDialog(view);
    }
  }

  public archiveTabView(view: SnMapView): void {
    this._mapViewsService
      .updateMapView(view.id, {
        ...view,
        archived: true,
      })
      .pipe(takeUntil(this._destroy$))
      .subscribe(() => {
        const index = this.views.findIndex((v) => v.id === view.id);
        if (index > -1) {
          this.views.splice(index, 1);
        }
      });
  }

  public deleteView(view: SnMapView): void {
    this._mapViewsService
      .deleteMapView(view)
      .pipe(takeUntil(this._destroy$))
      .subscribe(() => {
        const index = this.views.findIndex((v) => v.id === view.id);
        const cadIndex = this.activeCads.findIndex((c) => c.id === view.externalId);
        if (index > -1) {
          this.views.splice(index, 1);
        }
        if (cadIndex > -1) {
          this.activeCads.splice(cadIndex, 1);
        }
        this._cd.detectChanges();
      });
    this._cd.detectChanges();
  }

  private _openCameraSearchDialog(view: SnMapView, cameras?: SnCamera[]): void {
    this._dialog
      .open(CameraSearchDialogComponent, {
        data: {
          multiple: true,
          cameras,
        },
      })
      .afterClosed()
      .pipe(takeUntil(this._destroy$))
      .subscribe((results: SnCamera[]) => {
        if (results?.length) {
          this._updateView(view, results);
        }
      });
  }

  private _updateView(view: SnMapView, data: SnCamera[]): void {
    const ids = data.map((cam) => cam.id);
    this._mapViewsService
      .updateMapView(view.id, {
        ...view,
        cameras: ids,
      })
      .pipe(takeUntil(this._destroy$))
      .subscribe((mapView) => {
        view = mapView;
        const index = this.views.findIndex((v) => v.id === view.id);
        if (index > -1) {
          this.views[index] = view;
          this.activate(index + 1);
        }
      });
  }
}
